/**
 * Replaces variables in message templates with actual target data.
 * Supports the `{tag|fallback}` syntax used by profile-enrichment tags.
 */

/** Enrichment tags produced by the profile pre-fetch pipeline. */
export const ENRICHMENT_VARIABLES = [
  'current_title',
  'current_company',
  'industry',
  'location',
  'connections_count',
  'followers_count',
  'past_company',
  'years_at_company',
  'years_experience',
  'school',
  'top_recommendation',
  'top_recommendation_author',
  'company_size',
  'company_industry',
  'company_founded',
] as const;






const normalizeVariableName = (name: string): string =>
  name.toLowerCase().trim().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');

/** Split `{tag|fallback}` inner text into its parts. */
export const parseVariableToken = (raw: string): { name: string; fallback: string | null } => {
  const pipeIndex = raw.indexOf('|');
  if (pipeIndex < 0) return { name: raw, fallback: null };
  const fallback = raw.slice(pipeIndex + 1).trim();
  return { name: raw.slice(0, pipeIndex), fallback: fallback.length > 0 ? fallback : null };
};

export interface TemplateToken {
  /** Raw text including braces, e.g. `{industry|their space}`. */
  raw: string;
  /** Normalized variable name. */
  name: string;
  /** Fallback text after the pipe, if any. */
  fallback: string | null;
  /** Character offset of `{` in the template. */
  start: number;
  /** Character offset just past `}`. */
  end: number;
  isEnrichment: boolean;
}

/**
 * Every `{...}` token in a template with its offsets — the editor needs offsets
 * to highlight tokens and to rewrite a single occurrence in place.
 */
export const listTemplateTokens = (template: string): TemplateToken[] => {
  if (!template) return [];
  const tokens: TemplateToken[] = [];
  for (const match of template.matchAll(/\{([^}]*)\}/g)) {
    const { name, fallback } = parseVariableToken(match[1]);
    const normalized = normalizeVariableName(name);
    if (!normalized) continue;
    const start = match.index ?? 0;
    tokens.push({
      raw: match[0],
      name: normalized,
      fallback,
      start,
      end: start + match[0].length,
      isEnrichment: (ENRICHMENT_VARIABLES as readonly string[]).includes(normalized),
    });
  }
  return tokens;
};

/** Build a token string from a name and optional fallback. */
export const buildVariableToken = (name: string, fallback?: string | null): string => {
  const trimmed = (fallback ?? '').trim();
  return trimmed ? `{${name}|${trimmed}}` : `{${name}}`;
};

/** Replace the token occupying [start, end) with new text. */
export const replaceTokenAt = (
  template: string,
  start: number,
  end: number,
  replacement: string
): string => template.slice(0, start) + replacement + template.slice(end);

/** Update just the fallback of the token at [start, end). */
export const setTokenFallback = (
  template: string,
  token: TemplateToken,
  fallback: string
): string => replaceTokenAt(template, token.start, token.end, buildVariableToken(token.name, fallback));

/** Remove the token at [start, end), collapsing the double space it leaves. */
export const removeTokenAt = (template: string, token: TemplateToken): string =>
  replaceTokenAt(template, token.start, token.end, '').replace(/[ \t]{2,}/g, ' ');


/**
 * Enrichment tags are only known at send time, so the editor must require a
 * fallback for each one — otherwise a blank would ship to LinkedIn.
 */
export const findEnrichmentTagsMissingFallback = (template: string): string[] => {
  if (!template) return [];
  const missing: string[] = [];
  for (const match of template.matchAll(/\{([^}]+)\}/g)) {
    const { name, fallback } = parseVariableToken(match[1]);
    const normalized = normalizeVariableName(name);
    if ((ENRICHMENT_VARIABLES as readonly string[]).includes(normalized) && !fallback) {
      missing.push(normalized);
    }
  }
  return Array.from(new Set(missing));
};

/* ------------------------------------------------------------------ *
 * Fake placeholders
 *
 * Templates pasted from documents or other tools often use `[Name]`,
 * `<First Name>` or `((Company))`. Our engine only fills `{name}` tokens, so
 * anything else ships to LinkedIn verbatim (see the Vardhmaan Realtors
 * incident). These helpers detect and repair that before a send happens.
 * ------------------------------------------------------------------ */

/** Wrapper text -> the real variable it almost certainly meant. */
const FAKE_PLACEHOLDER_MAP: Record<string, string> = {
  name: 'first_name',
  full_name: 'name',
  fullname: 'name',
  first_name: 'first_name',
  firstname: 'first_name',
  fname: 'first_name',
  last_name: 'last_name',
  lastname: 'last_name',
  company: 'company',
  company_name: 'company',
  organisation: 'company',
  organization: 'company',
  title: 'title',
  job_title: 'title',
  position: 'title',
  role: 'title',
  industry: 'industry',
  location: 'location',
  city: 'location',
};

export interface FakePlaceholder {
  /** Raw text including the wrapper, e.g. `[First Name]`. */
  raw: string;
  /** Canonical variable this should have been. */
  suggested: string;
  start: number;
  end: number;
}

const FAKE_PLACEHOLDER_PATTERNS = [
  /\[([^\[\]\n]{1,30})\]/g,
  /<([^<>\n]{1,30})>/g,
  /\(\(([^()\n]{1,30})\)\)/g,
  /%%([^%\n]{1,30})%%/g,
];

/**
 * Placeholders that look like variables but will never be filled in.
 * Only wrappers whose inner text maps to a real variable are reported, so
 * ordinary bracketed prose is left alone.
 */
export const findFakePlaceholders = (template: string): FakePlaceholder[] => {
  if (!template) return [];
  const found: FakePlaceholder[] = [];
  for (const pattern of FAKE_PLACEHOLDER_PATTERNS) {
    for (const match of template.matchAll(pattern)) {
      const suggested = FAKE_PLACEHOLDER_MAP[normalizeVariableName(match[1])];
      if (!suggested) continue;
      const start = match.index ?? 0;
      found.push({ raw: match[0], suggested, start, end: start + match[0].length });
    }
  }
  return found.sort((a, b) => a.start - b.start);
};

/** Swap every detected fake placeholder for its real `{variable}` token. */
export const replaceFakePlaceholders = (template: string): string => {
  const found = findFakePlaceholders(template);
  let result = template;
  for (let i = found.length - 1; i >= 0; i--) {
    const p = found[i];
    result = result.slice(0, p.start) + `{${p.suggested}}` + result.slice(p.end);
  }
  return result;
};


/** Every enrichment tag used in a template (fallback syntax tolerated). */
export const findEnrichmentTagsUsed = (template: string): string[] => {
  if (!template) return [];
  const used: string[] = [];
  for (const match of template.matchAll(/\{([^}]+)\}/g)) {
    const normalized = normalizeVariableName(parseVariableToken(match[1]).name);
    if ((ENRICHMENT_VARIABLES as readonly string[]).includes(normalized)) used.push(normalized);
  }
  return Array.from(new Set(used));
};

export const replaceVariables = (
  template: string,
  target: any,
  enrichment?: Record<string, string | null> | null
): string => {
  if (!template || !target) return template;

  // Map of variable name variations to standard field names
  const standardFieldMappings: Record<string, string> = {
    'company_name': 'company',
    'company': 'company',
    'first_name': 'first_name',
    'firstname': 'first_name',
    'last_name': 'last_name',
    'lastname': 'last_name',
    'title': 'title',
    'job_title': 'title',
    'full_name': 'name',
    'name': 'name',
  };

  let result = template;
  const matches = template.matchAll(/\{([^}]+)\}/g);

  for (const match of matches) {
    const { name, fallback } = parseVariableToken(match[1]);
    const normalizedVar = normalizeVariableName(name);

    // Check if this is a variation of a standard field
    const standardField = standardFieldMappings[normalizedVar];

    // Try standard field mapping first
    let value = standardField ? target[standardField] : target[normalizedVar];

    // If not found, search in custom_data for a key that normalizes to the same value
    if ((value === undefined || value === null) && target.custom_data) {
      const customDataKey = Object.keys(target.custom_data).find(
        key => normalizeVariableName(key) === normalizedVar
      );
      if (customDataKey) value = target.custom_data[customDataKey];
    }

    // Profile enrichment values (preview only — real values resolve at send time)
    if ((value === undefined || value === null) && enrichment) {
      const enrichKey = Object.keys(enrichment).find(
        key => normalizeVariableName(key) === normalizedVar
      );
      if (enrichKey) value = enrichment[enrichKey];
    }

    if ((value === undefined || value === null || String(value).trim() === '') && fallback) {
      value = fallback;
    }

    if (value !== undefined && value !== null && String(value).trim() !== '') {
      result = result.replace(match[0], String(value));
    } else if ((ENRICHMENT_VARIABLES as readonly string[]).includes(normalizedVar)) {
      // Never leave a raw enrichment tag in the message.
      result = result.replace(match[0], '');
    }
  }

  return result.replace(/[ \t]{2,}/g, ' ').replace(/ +([,.!?])/g, '$1');
};
